import React from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/common/SEO';
import Layout from '../components/common/Layout';
import i18next from 'i18next';

// SVG Icons for features
const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

// Currency conversion rates (as of June 2025)
const CURRENCY_RATES = {
  USD: 1,
  VND: 24500 * 0.6, // 1 USD = 24,500 VND
  DKK: 6.8,   // 1 USD = 6.8 DKK
};

// Currency symbols and formatting
const CURRENCY_FORMATS = {
  USD: {
    symbol: '$',
    position: 'before',
    thousandSeparator: ',',
    decimalSeparator: '.',
    decimals: 2
  },
  VND: {
    symbol: '₫',
    position: 'after',
    thousandSeparator: '.',
    decimalSeparator: ',',
    decimals: 0
  },
  DKK: {
    symbol: 'kr',
    position: 'after',
    thousandSeparator: '.',
    decimalSeparator: ',',
    decimals: 2
  }
};

const PricingPage: React.FC = () => {
  const { t } = useTranslation();
  
  // Determine currency based on current language
  const currentLanguage = i18next.language || 'en';
  const currency = currentLanguage === 'vi' ? 'VND' : (currentLanguage === 'da' ? 'DKK' : 'USD');
  
  // Format price based on currency
  const formatPrice = (usdPrice: number): string => {
    if (usdPrice === 0) return t('pricing.plans.freeTrial.price');
    
    const format = CURRENCY_FORMATS[currency as keyof typeof CURRENCY_FORMATS];
    const rate = CURRENCY_RATES[currency as keyof typeof CURRENCY_RATES];
    
    const convertedPrice = usdPrice * rate;
    
    // Round to specified decimal places
    const roundedPrice = Number(convertedPrice.toFixed(format.decimals));
    
    // Format with thousand separators
    const parts = roundedPrice.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, format.thousandSeparator);
    const formattedPrice = parts.join(format.decimalSeparator);
    
    // Add currency symbol in correct position
    return format.position === 'before' ? 
      `${format.symbol}${formattedPrice}` : 
      `${formattedPrice} ${format.symbol}`;
  };

  // Define pricing plans with proper typing for features
  const getFeaturesArray = (key: string): string[] => {
    const features = t(key, { returnObjects: true });
    return Array.isArray(features) ? features.map(item => String(item)) : [];
  };

  const plans = [
    {
      id: 'freeTrial',
      name: t('pricing.plans.freeTrial.name'),
      duration: t('pricing.plans.freeTrial.duration'),
      price: {
        monthly: t('pricing.plans.freeTrial.price'),
        annual: t('pricing.plans.freeTrial.price'),
      },
      description: t('pricing.plans.freeTrial.description'),
      features: getFeaturesArray('pricing.plans.freeTrial.features'),
      buttonText: t('pricing.plans.freeTrial.buttonText'),
      highlighted: false,
    },
    {
      id: 'premium',
      name: t('pricing.plans.premium.name'),
      price: {
        monthly: formatPrice(4.99),
        annual: formatPrice(29.99),
      },
      description: currency === 'USD' ? 
        t('pricing.plans.premium.description') : 
        t('pricing.plans.premium.localDescription', { monthlyPrice: formatPrice(5) }),
      features: getFeaturesArray('pricing.plans.premium.features'),
      buttonText: t('pricing.plans.premium.buttonText'),
      highlighted: true,
    },
    {
      id: 'family',
      name: t('pricing.plans.family.name'),
      price: {
        annual: formatPrice(59.99),
      },
      description: t('pricing.plans.family.description'),
      features: getFeaturesArray('pricing.plans.family.features'),
      buttonText: t('pricing.plans.family.buttonText'),
      highlighted: false,
    },
    {
      id: 'lifetime',
      name: t('pricing.plans.lifetime.name'),
      price: {
        oneTime: formatPrice(99.99),
      },
      description: t('pricing.plans.lifetime.description'),
      features: getFeaturesArray('pricing.plans.lifetime.features'),
      buttonText: t('pricing.plans.lifetime.buttonText'),
      highlighted: false,
    },
  ];

  // State for toggling between monthly and annual billing
  const [billingCycle, setBillingCycle] = React.useState<'monthly' | 'annual'>('monthly');

  return (
    <Layout>
      <div className="bg-white">
        <SEO
          title={t('pricing.title')}
          description={t('pricing.subtitle')}
        />
        <div className="text-center">
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
            {t('pricing.title')}
          </h1>
          <p className="max-w-xl mt-5 mx-auto text-xl text-gray-500">
            {t('pricing.subtitle')}
          </p>
        </div>

        {/* Billing toggle */}
        <div className="relative flex justify-center mt-12">
          <div className="relative self-center bg-gray-100 rounded-lg p-0.5 flex">
            <button
              type="button"
              className={`${
                billingCycle === 'monthly' ? 'bg-white border-gray-200 shadow-sm text-gray-900' : 'border border-transparent text-gray-700'
              } relative w-1/2 rounded-md py-2 text-sm font-medium whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:z-10 sm:w-auto sm:px-8`}
              onClick={() => setBillingCycle('monthly')}
            >
              {t('pricing.monthlyBilling') || t('pricing.monthly')}
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-4 py-2 text-sm font-medium rounded-md ${billingCycle === 'annual' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            >
              {t('pricing.annualBilling') || t('pricing.annual')}
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {t('pricing.save') || t('pricing.savePercent')}
              </span>
            </button>
          </div>
        </div>

        {/* Pricing cards */}
        <div className="mt-12 space-y-4 sm:mt-16 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0 lg:mx-auto lg:max-w-4xl xl:mx-0 xl:max-w-none xl:grid-cols-4">
          {plans.map((plan) => {
            // Determine price to display based on billing cycle
            let displayPrice = plan.price.monthly;
            if (billingCycle === 'annual' && plan.price.annual) {
              displayPrice = plan.price.annual;
            }
            if (plan.price.oneTime) {
              displayPrice = plan.price.oneTime;
            }

            return (
              <div
                key={plan.name}
                className={`rounded-lg shadow-sm divide-y divide-gray-200 ${
                  plan.highlighted ? 'border-2 border-indigo-500 ring-2 ring-indigo-500' : 'border border-gray-200'
                }`}
              >
                <div className="p-6">
                  <h2 className="text-lg leading-6 font-medium text-gray-900">{plan.name}</h2>
                  {plan.duration && (
                    <p className="mt-1 text-sm text-indigo-600">{plan.duration}</p>
                  )}
                  <p className="mt-4">
                    <span className="text-4xl font-extrabold text-gray-900">{displayPrice}</span>
                    {displayPrice !== t('pricing.plans.freeTrial.price') && billingCycle === 'monthly' && plan.price.monthly && (
                      <span className="text-base font-medium text-gray-500">{t('pricing.perMonth')}</span>
                    )}
                    {displayPrice !== t('pricing.plans.freeTrial.price') && billingCycle === 'annual' && plan.price.annual && (
                      <span className="text-base font-medium text-gray-500">{t('pricing.perYear')}</span>
                    )}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">{plan.description}</p>
                  <button
                    type="button"
                    className={`mt-8 block w-full py-2 px-4 rounded-md shadow ${
                      plan.highlighted
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                        : 'bg-white text-indigo-600 border border-indigo-600 hover:bg-gray-50'
                    } text-center text-sm font-medium`}
                  >
                    {plan.buttonText}
                  </button>
                </div>
                <div className="pt-6 pb-8 px-6">
                  <h3 className="text-xs font-medium text-gray-900 tracking-wide uppercase">{t('pricing.whatsIncluded')}</h3>
                  <ul className="mt-6 space-y-4">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex space-x-3">
                        <CheckIcon />
                        <span className="text-sm text-gray-500">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto mt-20">
          <h2 className="text-3xl font-extrabold text-gray-900 text-center">{t('pricing.faqTitle')}</h2>
          <div className="mt-12">
            <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-12">
              <div>
                <dt className="text-lg leading-6 font-medium text-gray-900">{t('pricing.faq.switchPlans.question')}</dt>
                <dd className="mt-2 text-base text-gray-500">{t('pricing.faq.switchPlans.answer')}</dd>
              </div>
              <div>
                <dt className="text-lg leading-6 font-medium text-gray-900">{t('pricing.faq.refundPolicy.question')}</dt>
                <dd className="mt-2 text-base text-gray-500">{t('pricing.faq.refundPolicy.answer')}</dd>
              </div>
              <div>
                <dt className="text-lg leading-6 font-medium text-gray-900">{t('pricing.faq.familyPlan.question')}</dt>
                <dd className="mt-2 text-base text-gray-500">{t('pricing.faq.familyPlan.answer')}</dd>
              </div>
              <div>
                <dt className="text-lg leading-6 font-medium text-gray-900">{t('pricing.faq.freeTrial.question')}</dt>
                <dd className="mt-2 text-base text-gray-500">{t('pricing.faq.freeTrial.answer')}</dd>
              </div>
            </dl>
          </div>
        </div>
        
        {/* CTA Section */}
        <div className="bg-indigo-700 rounded-lg shadow-xl mt-20 mb-20">
          <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
              <span className="block">{t('pricing.ctaTitle')}</span>
            </h2>
            <p className="mt-4 text-lg leading-6 text-indigo-100">
              {t('pricing.ctaSubtitle')}
            </p>
            <a
              href="/signup"
              className="mt-8 w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-primary bg-white hover:bg-primary-light sm:w-auto"
            >
              {t('pricing.ctaButton')}
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PricingPage;
